import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const STATUS_STYLES = {
    pending:    { color: '#d97706', bg: '#fffbeb' },
    loaded:     { color: '#0891b2', bg: '#ecfeff' },
    in_transit: { color: '#3b82f6', bg: '#eff6ff' },
    delivered:  { color: '#059669', bg: '#ecfdf5' },
    cancelled:  { color: '#ef4444', bg: '#fef2f2' },
    default:    { color: '#6b7280', bg: '#f3f4f6' },
};

const StatusBadge = ({ status }) => {
    const s = STATUS_STYLES[status?.toLowerCase()] || STATUS_STYLES.default;
    return (
        <span style={{
            fontSize: 11, fontWeight: 700,
            color: s.color, backgroundColor: s.bg,
            borderRadius: 12, padding: '3px 9px',
            textTransform: 'capitalize',
        }}>
            {status?.replace(/_/g, ' ') || '—'}
        </span>
    );
};

const Th = ({ children, align = 'left' }) => (
    <th style={{
        padding: '10px 14px', fontSize: 11, fontWeight: 700,
        color: '#9ca3af', textTransform: 'uppercase',
        letterSpacing: '0.4px', textAlign: align,
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const Cell = ({ children, align = 'left', muted }) => (
    <td style={{
        padding: '10px 14px', fontSize: 13,
        color: muted ? '#9ca3af' : '#374151',
        fontWeight: muted ? 400 : 500,
        textAlign: align,
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children ?? '—'}
    </td>
);

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '—');

const formatDate = (d) => {
    if (!d) return '—';
    const clean = String(d).split('T')[0];
    try {
        return new Date(clean).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return clean;
    }
};

const ChallanListTable = ({ items = [], loading }) => {
    const { t } = useTranslation();
    return (
        <div style={cardStyle}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('common.analyticsWidgets.challanList.title')}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{t('common.analyticsWidgets.challanList.subtitle')}</div>
                </div>
                {items.length > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', backgroundColor: '#f3f4f6', borderRadius: 20, padding: '3px 10px' }}>
                        {items.length} records
                    </span>
                )}
            </div>

            {loading && <EmptyState message="Loading challan list..." />}
            {!loading && !items.length && <EmptyState message="No challans found for the selected filters." />}

            {!loading && items.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
                        <thead>
                            <tr>
                                <Th>#</Th>
                                <Th>{t('common.analyticsColumns.challanNo')}</Th>
                                <Th>{t('common.analyticsColumns.customer')}</Th>
                                <Th>{t('common.analyticsColumns.supervisor')}</Th>
                                <Th>{t('common.analyticsColumns.vehicle')}</Th>
                                <Th>{t('common.analyticsColumns.status')}</Th>
                                <Th align="right">{t('common.analyticsColumns.weight')}</Th>
                                <Th>{t('common.analyticsColumns.date')}</Th>
                                <Th align="center">{t('common.analyticsColumns.printed')}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row, i) => (
                                <tr
                                    key={row.challan_number ?? row.id ?? i}
                                    style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f9ff')}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#ffffff' : '#fafafa')}
                                >
                                    <Cell muted>{i + 1}</Cell>
                                    <Cell>
                                        <span style={{ fontWeight: 700, color: '#0891b2' }}>
                                            {row.challan_number ?? row.challan_no ?? '—'}
                                        </span>
                                    </Cell>
                                    <Cell>{row.customer_name ?? row.customer ?? '—'}</Cell>
                                    <Cell muted>{row.supervisor_name ?? row.supervisor ?? '—'}</Cell>
                                    <Cell muted>{row.vehicle_number ?? row.vehicle ?? '—'}</Cell>
                                    <Cell><StatusBadge status={row.status} /></Cell>
                                    <Cell align="right">
                                        {row.total_weight_kg != null
                                            ? `${fmt(row.total_weight_kg)} kg`
                                            : row.weight_tons != null
                                                ? `${parseFloat(row.weight_tons).toFixed(2)} T`
                                                : '—'}
                                    </Cell>
                                    <Cell muted>
                                        {formatDate(row.challan_date ?? row.date ?? row.created_at)}
                                    </Cell>
                                    <Cell align="center">
                                        {row.challan_printed || row.is_printed ? (
                                            <FontAwesomeIcon icon={faPrint} style={{ color: '#059669', fontSize: 13 }} />
                                        ) : (
                                            <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>
                                        )}
                                    </Cell>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const EmptyState = ({ message }) => (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{message}</div>
);

const cardStyle = {
    backgroundColor: '#fff', borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};

export default ChallanListTable;
